import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  User,
  ArrowLeft,
  Mail,
  Phone,
  Shield,
  Edit3,
  Trash2,
  FileText,
  Boxes,
} from "lucide-react";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { getRoleMeta, getStatusMeta, formatPhone } from "./shared";

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Customer Details
  const fetchMember = async () => {
    loader(true);
    setLoading(true);

    try {
      const res = await ApiClient.get("tenants/portal/team/detail", { id }, "", true);

      if (res?.success || res?.data || res?.member) {
        setMember(res.data?.member || res.data || res.member);
      } else {
        toast.error("Customer profile not found");
        navigate("/tenant/customers");
      }
    } catch (err) {
      toast.error("Failed to load customer profile");
      navigate("/tenant/customers");
    } finally {
      loader(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchMember();
  }, [id]);

  // Toggle Customer Status
  const handleToggleStatus = async () => {
    if (!member) return;
    const currentStatus = (member.status || "active").toLowerCase();
    const newStatus = currentStatus === "active" ? "deactive" : "active";

    try {
      loader(true);
      const res = await ApiClient.post(
        "tenants/portal/team/status",
        { id, status: newStatus },
        "",
        true
      );

      if (res?.success || !res?.error) {
        toast.success(`Customer status updated to ${newStatus}`);
        setMember((prev) => ({ ...prev, status: newStatus }));
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error("Error updating status");
    } finally {
      loader(false);
    }
  };

  // Delete Customer
  const handleDelete = async () => {
    const fullName = `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || member?.email;

    const willDelete = await swal({
      title: "Remove Customer?",
      text: `Are you sure you want to remove ${fullName}? Portal access will be revoked immediately.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Remove"],
      dangerMode: true,
    });

    if (willDelete) {
      try {
        loader(true);
        const res = await ApiClient.delete("tenants/portal/team/delete", { id }, "", true);
        if (res?.success || !res?.error) {
          toast.success("Customer removed successfully");
          navigate("/tenant/customers");
        } else {
          toast.error(res?.message || "Failed to remove customer");
        }
      } catch (err) {
        toast.error("Error removing customer");
      } finally {
        loader(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4F7FB]">
        <Header />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-600">Loading customer profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!member) return null;

  const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Customer";
  const roleMeta = getRoleMeta(member.roleName || member.role);
  const statusMeta = getStatusMeta(member.status);
  const initials = ((member.firstName?.[0] || "") + (member.lastName?.[0] || "")).toUpperCase() || "CU";

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      {/* Global standard container matching Dashboard: max-w-5xl with mt-[60px] lg:mt-[70px] */}
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 pb-32">
          {/* Navigation Breadcrumb */}
          <Link
            to="/tenant/customers"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-[#115B9D] transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Back to Customers</span>
          </Link>

          {/* ─── TOP PROFILE HEADER CARD ─── */}
          <div className="bg-white rounded-3xl border border-slate-300 shadow-sm p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#115B9D] text-white font-black text-xl flex items-center justify-center shadow-md shadow-[#115B9D]/20 shrink-0">
                  {initials}
                </div>

                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                      {fullName}
                    </h1>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${statusMeta.badgeClass}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dotClass}`} />
                      <span>{statusMeta.label}</span>
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-bold border ${roleMeta.badgeClass}`}
                    >
                      <Shield size={11} />
                      <span>{roleMeta.label}</span>
                    </span>
                    <span>
                      Customer ID: <strong className="text-slate-700 font-mono text-[11px]">{id}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2.5 self-start sm:self-auto flex-wrap">
                <button
                  type="button"
                  onClick={handleToggleStatus}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                    statusMeta.isActive
                      ? "border-rose-300 text-rose-700 hover:bg-rose-50"
                      : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {statusMeta.isActive ? "Deactivate Customer" : "Activate Customer"}
                </button>

                <Link
                  to={`/tenant/customers/edit/${id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold shadow-xs transition-colors"
                >
                  <Edit3 size={14} />
                  <span>Edit Customer</span>
                </Link>

                <button
                  type="button"
                  onClick={handleDelete}
                  className="p-2 rounded-xl border border-rose-300 text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                  title="Remove Customer"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* ─── DETAILS & PERMISSIONS GRID ─── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Column 1 & 2: Contact & Account Info */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-white rounded-3xl border border-slate-300 shadow-sm p-6">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-4">
                  Contact &amp; Account Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Email Address
                    </span>
                    <div className="font-bold text-slate-900 flex items-center gap-2 truncate">
                      <Mail size={14} className="text-[#115B9D] shrink-0" />
                      <span className="truncate">{member.email || "—"}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Mobile Phone
                    </span>
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <Phone size={14} className="text-[#115B9D] shrink-0" />
                      <span>{formatPhone(member.mobileNo || member.phone)}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Assigned Role
                    </span>
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <Shield size={14} className="text-[#115B9D] shrink-0" />
                      <span>{roleMeta.label}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Account Status
                    </span>
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusMeta.dotClass}`} />
                      <span>{statusMeta.label}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Portal Capabilities */}
              <div className="bg-white rounded-3xl border border-slate-300 shadow-sm p-6">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">
                  Customer Portal Privileges
                </h3>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-800">
                    <FileText size={15} className="text-[#115B9D]" />
                    <span className="font-medium">Can submit customer quote inquiries and request estimate revisions</span>
                  </div>
                  <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-800">
                    <Boxes size={15} className="text-[#115B9D]" />
                    <span className="font-medium">Can search product catalog inventory, pricing models, and specifications</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: Quick Info Sidebar Card */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl border border-slate-300 shadow-sm p-6 space-y-4">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500">
                  Account Quota &amp; Security
                </h3>

                <div className="space-y-3 text-xs text-slate-700">
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500">Role Type:</span>
                    <strong className="text-slate-900">User (Customer)</strong>
                  </div>

                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-500">Portal Access:</span>
                    <strong className="text-slate-900">Active</strong>
                  </div>

                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-slate-500">Quota Consumption:</span>
                    <strong className="text-slate-900">1 Customer Seat</strong>
                  </div>
                </div>

                <div className="pt-2">
                  <Link
                    to="/tenant/customers"
                    className="w-full py-2 px-3 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors block text-center"
                  >
                    Return to Customers
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
